import React from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import LocationGallery from '../components/LocationGallery';
import QuoteSection from '../components/QuoteSection';
import QuickQuoteForm from '../components/QuickQuoteForm';

const LawnCareMiddletownPage = () => {
  const packages = [
    {
      id: 1,
      name: 'Middletown Basic',
      price: '$40/visit',
      description: 'Essential lawn care for Middletown homes',
      features: [
        'Weekly mowing during growing season',
        'Edge trimming around walkways',
        'Grass clipping cleanup',
        'Basic weed spot treatment',
        'Reliable Middletown service'
      ]
    },
    {
      id: 2,
      name: 'Middletown Premium',
      price: '$65/visit',
      description: 'Complete lawn maintenance for Middletown properties',
      features: [
        'Weekly mowing and edging',
        'Trimming around all structures',
        'Leaf removal and cleanup',
        'Fertilization program',
        'Weed and pest control',
        'Seasonal lawn treatments',
        'Priority scheduling'
      ],
      popular: true
    },
    {
      id: 3,
      name: 'Middletown Commercial',
      price: 'Custom Pricing',
      description: 'Professional lawn care for Middletown businesses',
      features: [
        'Commercial-grade equipment',
        'Scheduled weekly maintenance',
        'Professional appearance standards',
        'Landscaping bed maintenance',
        'Seasonal cleanup services',
        'Liability insurance included',
        'Emergency response available'
      ]
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "name": "Lawn Care Service Middletown CT - G&D Landscaping",
    "description": "Professional lawn care and maintenance services in Middletown, Connecticut. Weekly mowing, fertilization, and complete yard care for homes and businesses.",
    "address": {
      "@type": "PostalAddress",
      "addressLocality": "Middletown",
      "addressRegion": "CT",
      "addressCountry": "US"
    },
    "telephone": "(860) 526-7583",
    "url": "https://www.gdlandscapingllc.com/lawn-care-middletown-ct"
  };

  const overviewHighlights = [
    'Weekly schedules built around university traffic keep Wesleyan-area clients serviced before campus crowds fill the streets.',
    'Higher-cut programs for the Highlands and Laurel Grove protect turf health on rocky soil while maintaining crisp stripes.',
    'Add-on pruning, mulch refresh, and seasonal cleanup keep Middlefield Street and Westfield neighborhoods show-ready year-round.'
  ];

  const serviceStats = [
    { value: '14', label: 'Middletown routes' },
    { value: '25 min', label: 'Average dispatch' },
    { value: '120+', label: 'Acres maintained' }
  ];

  return (
    <>
      <SEOHead
        title="Lawn Care Middletown CT | G&D Landscaping"
        description="Professional lawn care services in Middletown, Connecticut. Weekly mowing, fertilization, weed control, and complete yard maintenance for all Middletown areas."
        keywords="lawn care Middletown CT, lawn mowing Middletown Connecticut, landscaping Middletown, yard maintenance Middletown CT, fertilization Middletown, weed control Middletown"
        canonicalUrl="https://www.gdlandscapingllc.com/lawn-care-middletown-ct"
        structuredData={structuredData}
      />

      <div className="lawn-care-section">
        <div className="lawn-hero">
          <div className="lawn-hero-video">
            <video autoPlay muted loop playsInline preload="auto" poster="/garden-design-gd-landscaping.jpeg">
              <source src="/AdobeStock_657294798.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>
          </div>
          <div className="lawn-hero-overlay"></div>
          <div className="lawn-hero-content">
            <div className="container">
              <div className="hero-badge">
                <span>🌱 Serving Middletown, CT</span>
              </div>
              <h1>Lawn Care Service Middletown CT</h1>
              <p className="hero-subtitle">Professional lawn maintenance for Middletown residents and businesses. Keep your university town property looking exceptional with expert lawn care services.</p>

              <QuickQuoteForm source="lawn-care-middletown" />

              <div className="hero-buttons">
                <Link to="/contact" className="cta-primary">
                  Get Middletown Quote
                </Link>
                <a href="tel:(860) 526-7583" className="cta-secondary">
                  Call (860) 526-7583
                </a>
              </div>
            </div>
          </div>
        </div>

        <LocationGallery
          townName="Middletown, CT"
          subtitle="Maintenance highlights from Wesleyan Hill to the Highlands and Westfield districts."
        />

        <section className="town-overview">
          <div className="container">
            <div className="town-overview-grid">
              <div className="town-overview-card">
                <h2>Middletown Insight, Professional Finish</h2>
                <p>College-town lawns see constant activity, shade, and irrigation challenges. We blend university-area expectations with quieter suburban neighborhoods to deliver the same neat, upscale curb appeal across Middletown.</p>
                <ul>
                  {overviewHighlights.map((item, idx) => (
                    <li key={`middletown-highlight-${idx}`}>
                      <span>✓</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="town-overview-card">
                <h3>Middletown Service Snapshot</h3>
                <p>Crews stage minutes from Main Street for rapid deployment when weather shifts.</p>
                <div className="town-stats">
                  {serviceStats.map((stat) => (
                    <div key={stat.label} className="town-stat">
                      <strong>{stat.value}</strong>
                      <span>{stat.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="container">
          <section className="packages-section">
            <div className="section-header">
              <h2>Professional Lawn Care Services</h2>
              <p className="section-subtitle">Professional lawn maintenance plans for Middletown properties</p>
            </div>

            <div className="packages-grid">
              {packages.map((pkg) => (
                <div key={pkg.id} className={`package-card ${pkg.popular ? 'popular' : ''}`}>
                  {pkg.popular && <div className="popular-badge">Most Popular in Middletown</div>}
                  <div className="package-header">
                    <h3>{pkg.name}</h3>
                    <div className="package-price">{pkg.price}</div>
                    <p className="package-description">{pkg.description}</p>
                  </div>
                  <div className="package-features">
                    <ul>
                      {pkg.features.map((feature, index) => (
                        <li key={index}>
                          <span className="feature-check">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="package-footer">
                    <Link to="/contact" className="package-btn">
                      Get Middletown Quote
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </section>
            <QuoteSection
            title="Tell Us About Your Middletown Property"
            subtitle="Prefer the traditional route? Share your details and we’ll send a custom lawn care estimate shortly."
            locationName="Middletown"
            source="Middletown Lawn Page"
          />

          <section className="lawn-cta">
            <div className="cta-content">
              <h2>Ready for a Beautiful Lawn in Middletown?</h2>
              <p>Join your Middletown neighbors who trust G&D Landscaping for professional lawn care.</p>
              <div className="cta-buttons">
                <Link to="/contact" className="cta-primary">
                  Get Your Middletown Quote
                </Link>
                <a href="tel:(860) 526-7583" className="cta-secondary">
                  Call (860) 526-7583
                </a>
              </div>
            </div>
          </section>
        </div>
      </div>
    </>
  );
};

export default LawnCareMiddletownPage;
